/**
 * Bounded private My Music search for Party Search.
 *
 * Searches only the signed-in user's imported playlists through existing
 * cache-first library APIs. Does not copy private items into Recordings,
 * ProviderAssets, or catalogSearchDocuments.
 *
 * Scope: the first cached/server page of each of the first
 * MY_MUSIC_SEARCH_PLAYLIST_LIMIT imported playlists. Does not page through
 * an entire library and does not add a private search index.
 */

import { fetchImportedPlaylists } from './importedPlaylists';
import {
  isPlayableLibraryItem,
  libraryItemArtistSubtitle,
  libraryItemDisplayTitle,
  libraryItemToQueueTrack,
  readUserPlaylistItemsPage,
} from './userLibrary';
import {
  normalizePartySearchQuery,
  textMatchesNormalizedQuery,
} from './partySearchNormalize';

export const MY_MUSIC_SEARCH_PLAYLIST_LIMIT = 8;
export const MY_MUSIC_SEARCH_RESULT_LIMIT = 10;

function matchLibraryItem(item, normalizedQuery) {
  const titleHit = textMatchesNormalizedQuery(
    libraryItemDisplayTitle(item),
    normalizedQuery,
  );
  if (titleHit) {
    return { field: 'title', strength: titleHit };
  }
  const artistHit = textMatchesNormalizedQuery(
    libraryItemArtistSubtitle(item),
    normalizedQuery,
  );
  if (artistHit) {
    return { field: 'artist', strength: artistHit };
  }
  return null;
}

function toMyMusicSearchResult(item) {
  const queueTrack = libraryItemToQueueTrack(item);
  const playable = Boolean(queueTrack?.playable && isPlayableLibraryItem(item));
  return {
    resultId: `my:${queueTrack.userPlaylistItemId || queueTrack.playlistItemId || queueTrack.id}`,
    source: 'my_music',
    identityType: 'playlist_occurrence',
    displayTitle: queueTrack.title,
    thumbnail: queueTrack.thumbnail || '',
    providerAssetKey: queueTrack.providerAssetKey || null,
    playlistOccurrenceId: queueTrack.playlistItemId || null,
    playability: {
      state: playable ? 'playable' : 'unavailable',
    },
    actions: {
      queue: playable,
      playNow: false,
      playNext: false,
      requestAvailability: false,
      openOccurrence: true,
    },
    queueTrack: playable ? queueTrack : null,
  };
}

export async function searchMyMusicLibrary(rawQuery, {
  uid,
  fetchPlaylists = fetchImportedPlaylists,
  readPlaylistPage = readUserPlaylistItemsPage,
  playlistLimit = MY_MUSIC_SEARCH_PLAYLIST_LIMIT,
  resultLimit = MY_MUSIC_SEARCH_RESULT_LIMIT,
} = {}) {
  const normalizedQuery = normalizePartySearchQuery(rawQuery);
  if (!uid || !normalizedQuery.collapsedWhitespace) {
    return { results: [], limited: true };
  }

  let playlists = [];
  try {
    playlists = await fetchPlaylists(uid);
  } catch {
    return { results: [], limited: true };
  }
  if (!Array.isArray(playlists) || playlists.length === 0) {
    return { results: [], limited: true };
  }

  const scoped = playlists.slice(0, playlistLimit);
  const pages = await Promise.all(scoped.map(async (playlist) => {
    try {
      return await readPlaylistPage({ uid, playlist });
    } catch {
      return { items: [] };
    }
  }));

  const matches = [];
  const seenOccurrence = new Set();
  pages.forEach((page) => {
    const items = Array.isArray(page?.items) ? page.items : [];
    items.forEach((item) => {
      if (matches.length >= resultLimit) return;
      const occurrenceKey = item?.userPlaylistItemId
        || item?.providerPlaylistItemId
        || item?.providerAssetKey;
      if (!occurrenceKey || seenOccurrence.has(occurrenceKey)) return;
      if (!matchLibraryItem(item, normalizedQuery)) return;
      seenOccurrence.add(occurrenceKey);
      matches.push(toMyMusicSearchResult(item));
    });
  });

  return {
    results: matches,
    limited: playlists.length > playlistLimit,
  };
}
